import { Injectable } from "@angular/core";
import { ELogActionId } from "@calaosoft/osapp-common/logging/models/ELogActionId";
import { EDatabaseRole } from "@calaosoft/osapp-common/store/models/edatabase-role";
import { IDataSource } from "@calaosoft/osapp-common/store/models/IDataSource";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { EPrefix } from "@calaosoft/osapp-common/utils/models/EPrefix";
import { firstValueFrom } from "rxjs";
import { EStoreFlag } from "../../../model/store/EStoreFlag";
import { ITaskDescriptor as IOldTaskDescriptor } from "../../../services/backgroundTask/itask-descriptor";
import { FlagService } from "../../../services/flag.service";
import { Store } from "../../../services/store.service";
import { LoggerService } from "../../logger/services/logger.service";
import { PatchBase } from "../../patch/patches/patch-base";
import { EBTSFlag } from "../models/ebts-flag";
import { BackgroundTaskDatabaseService } from "../services/background-task-database.service";

interface IOldTaskDescriptorsResult {
	readonly oldDescriptors: IOldTaskDescriptor[] | undefined;
	readonly ok: boolean;
	readonly error?: any;
}
interface IMigrateOldDescriptorsResult {
	readonly migratedCount: number;
	readonly notMigratedDescriptors: ReadonlyArray<IOldTaskDescriptor>;
	readonly ok: boolean;
}

const BEGIN_ACION_ID = "migrate-bts-storage-patch-begin";
const ERROR_GET_OLD_DESCRIPTORS_ACTION_ID = "migrate-bts-storage-patch-error-get-old-descriptors";
const ERROR_MIGRATE_OLD_DESCRIPTOR_ACTION_ID = "migrate-bts-storage-patch-error-migrate-old-descriptor";
const END_ACION_ID = "migrate-bts-storage-patch-end";
const END_ERROR_ACION_ID = "migrate-bts-storage-patch-end-error";

@Injectable()
export class MigrateBTSStoragePatch extends PatchBase {
	//#region FIELDS

	private static readonly C_LOG_ID = "MGRT.BTS.STRG.PTCH::";
	private static readonly C_STORAGE_KEY = "bts_storage_migration_done";

	//#endregion FIELDS

	//#region METHODS

	constructor(
		private readonly isvcLogger: LoggerService,
		private readonly isvcStore: Store,
		private readonly isvcBackgroundTaskDatabase: BackgroundTaskDatabaseService,
		private readonly isvcFlag: FlagService
	) {
		super("Migration des données de stockage des tâches en arrière-plan");
	}

	//todo : Lors de la suppression du patch, supprimer toutes les occurrences de "IOldTaskDescriptor" et son fichier associé.
	/** @override */
	public override async applyPatchAsync(): Promise<boolean> {
		const lsPatched: string | null = localStorage.getItem(MigrateBTSStoragePatch.C_STORAGE_KEY);

		if (lsPatched) {
			// Le patch a déjà été appliqué.
			this.isvcFlag.setFlagValue(EBTSFlag.BTSStorageMigrationDone, true);
			return true;
		} else {
			try {
				await this.waitFlagsBeforeBeginAsync();
				const lbMigrated: boolean = await this.migrateAsync();
				// Peu importe la valeur, juste pour indiquer que le patch a été appliqué.
				localStorage.setItem(MigrateBTSStoragePatch.C_STORAGE_KEY, "ok");
				return lbMigrated;
			} catch (poError) {
				console.error(`${MigrateBTSStoragePatch.C_LOG_ID}Error during migration of old task descriptors:`, poError);
				return false;
			} finally {
				this.isvcFlag.setFlagValue(EBTSFlag.BTSStorageMigrationDone, true);
			}
		}
	}

	private waitFlagsBeforeBeginAsync(): Promise<void> {
		// Il faut attendre que la vieille base de stockage et la nouvelle soient initialisées avant de commencer la migration.
		return this.isvcFlag.waitForFlagsAsync(
			[EBTSFlag.BTSDatabaseInitialized, EStoreFlag.DBLocalInitialized],
			[true, true]
		);
	}

	private async migrateAsync(): Promise<boolean> {
		this.logBeginAction();

		const loOldDescriptorsResult: IOldTaskDescriptorsResult = await this.getOldDescriptorsResultAsync();

		if (!loOldDescriptorsResult.ok) {
			this.logErrorGetOldDescriptorsAction(loOldDescriptorsResult.error);
			return false;
		} else {
			if (ArrayHelper.hasElements(loOldDescriptorsResult.oldDescriptors)) {
				const loMigrationResult: IMigrateOldDescriptorsResult = await this.migrateOldDescriptorsAsync(
					loOldDescriptorsResult.oldDescriptors
				);

				this.logEndAction(loMigrationResult);

				return loMigrationResult.ok;
			} else {
				this.logEndAction();
				return true;
			}
		}
	}

	private async getOldDescriptorsResultAsync(): Promise<IOldTaskDescriptorsResult> {
		try {
			const paOldDescriptors = await firstValueFrom(this.isvcStore.get<IOldTaskDescriptor>(this.getDataSource()));
			return { oldDescriptors: paOldDescriptors, ok: true };
		} catch (poError) {
			return { oldDescriptors: undefined, ok: false, error: poError };
		}
	}

	private getDataSource(): IDataSource {
		return {
			databasesIds: this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.applicationStorage),
			viewParams: {
				startkey: EPrefix.task,
				endkey: `${EPrefix.task}${Store.C_ANYTHING_CODE_ASCII}`,
				include_docs: true
			}
		};
	}

	private async migrateOldDescriptorsAsync(
		paOldDescriptors: IOldTaskDescriptor[]
	): Promise<IMigrateOldDescriptorsResult> {
		const laNotMigratedDescriptors: IOldTaskDescriptor[] = [];

		for (let lnIndex = 0; lnIndex < paOldDescriptors.length; ++lnIndex) {
			const loOldDescriptor: IOldTaskDescriptor = paOldDescriptors[lnIndex];
			const lbMigrated: boolean = await this.migrateOldDescriptorAsync(loOldDescriptor);

			if (!lbMigrated) laNotMigratedDescriptors.push(loOldDescriptor);
		}

		return {
			migratedCount: paOldDescriptors.length - laNotMigratedDescriptors.length,
			notMigratedDescriptors: laNotMigratedDescriptors,
			ok: laNotMigratedDescriptors.length === 0
		};
	}

	private async migrateOldDescriptorAsync(poOldDescriptor: IOldTaskDescriptor): Promise<boolean> {
		try {
			// Enregistrement du nouveau descripteur dans la nouvelle base de données.
			await this.isvcBackgroundTaskDatabase.putAsync(poOldDescriptor);
			console.debug(
				`${MigrateBTSStoragePatch.C_LOG_ID}Old descriptor '${poOldDescriptor._id}' (${poOldDescriptor.name}) migrated successfully.`
			);
		} catch (poError) {
			this.isvcLogger.action(
				MigrateBTSStoragePatch.C_LOG_ID,
				`Error put old descriptor '${poOldDescriptor._id}' (${poOldDescriptor.name}) in new database`,
				ERROR_MIGRATE_OLD_DESCRIPTOR_ACTION_ID as ELogActionId,
				undefined,
				poError
			);
			return false;
		}

		try {
			// Suppression de l'ancien descripteur.
			await this.isvcStore.deleteAsync(poOldDescriptor);
			console.debug(
				`${MigrateBTSStoragePatch.C_LOG_ID}Old descriptor '${poOldDescriptor._id}' (${poOldDescriptor.name}) deleted successfully.`
			);
		} catch (poError) {
			this.isvcLogger.action(
				MigrateBTSStoragePatch.C_LOG_ID,
				`Error delete old descriptor '${poOldDescriptor._id}' (${poOldDescriptor.name}) from old database`,
				ERROR_MIGRATE_OLD_DESCRIPTOR_ACTION_ID as ELogActionId,
				undefined,
				poError
			);
			return false;
		}

		return true;
	}

	//#region Logs

	private logBeginAction(): void {
		this.isvcLogger.action(
			MigrateBTSStoragePatch.C_LOG_ID,
			`Patch "${this.patchDescription}" begins.`,
			BEGIN_ACION_ID as ELogActionId
		);
	}

	private logErrorGetOldDescriptorsAction(poError: any): void {
		this.isvcLogger.action(
			MigrateBTSStoragePatch.C_LOG_ID,
			`Error getting old descriptors :`,
			ERROR_GET_OLD_DESCRIPTORS_ACTION_ID as ELogActionId,
			undefined,
			poError
		);
	}

	private logEndAction(poMigrationResult?: IMigrateOldDescriptorsResult): void {
		if (poMigrationResult) {
			const lsNotMigratedMessage = poMigrationResult.ok
				? ""
				: ` But ${poMigrationResult.notMigratedDescriptors.length} failed : [${poMigrationResult.notMigratedDescriptors.map((poOld: IOldTaskDescriptor) => `${poOld._id} (${poOld.name})`)}].`;

			this.isvcLogger.action(
				MigrateBTSStoragePatch.C_LOG_ID,
				`Migration of ${poMigrationResult.migratedCount} old task descriptors with success.${lsNotMigratedMessage}`,
				poMigrationResult.ok ? (END_ACION_ID as ELogActionId) : (END_ERROR_ACION_ID as ELogActionId)
			);
		} else {
			this.isvcLogger.action(
				MigrateBTSStoragePatch.C_LOG_ID,
				`Patch "${this.patchDescription}" ends without treatment.`,
				END_ACION_ID as ELogActionId
			);
		}
	}

	//#endregion Logs

	//#endregion
}
